// Operation date/time helpers — schema stores start_date / end_date
// ('YYYY-MM-DD' Bangkok-local) and start_time / end_time ('HH:MM').
// Use these so display code never has to do raw parsing.

const TH_DATE_OPTS = { day: '2-digit', month: 'short', year: 'numeric' }

// Lex-compare two date+time pairs. >0 a>b · 0 equal · <0 a<b.
export function cmpDateTime(d1, t1, d2, t2) {
  if (d1 !== d2) return d1 < d2 ? -1 : 1
  if (t1 !== t2) return t1 < t2 ? -1 : 1
  return 0
}

// Bangkok local date+time → unix seconds. Used for FullCalendar / image editor.
export function bkkToUnixSec(date, time) {
  if (!date || !time) return 0
  return Math.floor(Date.parse(`${date}T${time}:00+07:00`) / 1000)
}

export function fmtDateOnly(date) {
  if (!date) return '—'
  // Build a UTC date at midnight from YYYY-MM-DD so toLocaleDateString shows
  // the same Bangkok day in any browser.
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('th-TH-u-ca-gregory', TH_DATE_OPTS)
}

export function fmtTimeOnly(time) {
  return time || '—'
}

export function fmtDateTime(date, time) {
  if (!date) return '—'
  return fmtDateOnly(date) + (time ? ' · ' + time : '')
}

// "23 พ.ค. 2026 · 09:00 – 17:00"    (same-day range)
// "23 → 25 พ.ค. 2026 · 09:00 – 17:00" (multi-day range)
export function fmtRange(startDate, endDate, startTime, endTime) {
  if (!startDate) return '—'
  const sd = fmtDateOnly(startDate)
  const same = endDate && endDate === startDate
  if (same) {
    let s = sd
    if (startTime) s += ' · ' + startTime
    if (endTime)   s += ' – ' + endTime
    return s
  }
  if (!endDate) return sd + (startTime ? ' · ' + startTime : '')
  let s = sd
  if (startTime) s += ' · ' + startTime
  s += ' → ' + fmtDateOnly(endDate)
  if (endTime) s += ' · ' + endTime
  return s
}

// Buddhist-Era variant used on the public registration card.
// Single day : "30 พ.ค. 2569 9:00 - 17:00"
// Multi day  : "30 พ.ค. - 3 มิ.ย. 2569 9:00 - 17:00"
// If the year (BE) differs between start and end, both years are shown.
const TH_MONTHS_ABBR = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
function stripLeadingZeroHM(t) {
  if (!t) return ''
  const [h, m] = t.split(':')
  return `${Number(h)}:${m ?? '00'}`
}
export function fmtRangeBE(startDate, endDate, startTime, endTime) {
  if (!startDate) return '—'
  const [sy, sm, sd] = startDate.split('-').map(Number)
  const startBE = sy + 543
  const startMonth = TH_MONTHS_ABBR[sm - 1]

  const time = (startTime || endTime)
    ? `เวลา ${stripLeadingZeroHM(startTime) || '?'} - ${stripLeadingZeroHM(endTime) || '?'}`
    : ''

  if (!endDate || endDate === startDate) {
    const datePart = `${sd} ${startMonth} ${startBE}`
    return time ? `${datePart} ${time}` : datePart
  }

  const [ey, em, ed] = endDate.split('-').map(Number)
  const endBE = ey + 543
  const endMonth = TH_MONTHS_ABBR[em - 1]

  let datePart
  if (startBE !== endBE) {
    datePart = `${sd} ${startMonth} ${startBE} - ${ed} ${endMonth} ${endBE}`
  } else if (sm !== em) {
    datePart = `${sd} ${startMonth} - ${ed} ${endMonth} ${endBE}`
  } else {
    datePart = `${sd} - ${ed} ${endMonth} ${endBE}`
  }
  return time ? `${datePart} ${time}` : datePart
}
